// Supabase service for English Writing Tools
const SUPABASE_URL = 'https://eximyshafkuyehkiotuf.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4aW15c2hhZmt1eWVoa2lvdHVmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTUxODc0MDEsImV4cCI6MjA3MDc2MzQwMX0.bjrI5MO_WUfnI53UaQ_m8BS8LPh2et5OrqMKmWUvYzw';

/**
 * Fetch data from Supabase using REST API
 */
const supabaseFetch = async (table, filters = {}, orderBy = 'sort_order', orderDirection = 'asc', limit = 1000) => {
  try {
    let url = `${SUPABASE_URL}/rest/v1/${table}?select=*`;
    
    // Add filters
    if (Object.keys(filters).length > 0) {
      const filterParams = Object.entries(filters)
        .map(([key, value]) => {
          // Handle boolean values
          if (typeof value === 'boolean') {
            return `${key}=eq.${value}`;
          }
          return `${key}=eq.${encodeURIComponent(value)}`;
        })
        .join('&');
      url += `&${filterParams}`;
    }
    
    // Add ordering
    url += `&order=${orderBy}.${orderDirection}`;
    
    // Add limit
    if (limit) {
      url += `&limit=${limit}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Supabase query failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Supabase fetch error:', error);
    throw error;
  }
};

/**
 * Fetch language data from Supabase
 */
export const fetchLanguageData = async () => {
  try {
    const data = await supabaseFetch(
      'eng_writingtools_examples',
      { data_type: 'language_data', is_active: true },
      'sort_order',
      'asc',
      1
    );
    
    if (data && data.length > 0 && data[0].data_json) {
      return data[0].data_json;
    }
    
    throw new Error('No language data found');
  } catch (error) {
    console.error('Error fetching language data:', error);
    throw error;
  }
};

/**
 * Fetch all examples from Supabase and transform to expected format
 */
export const fetchExamples = async () => {
  try {
    // Fetch all example records (excluding language_data)
    const allRecords = await supabaseFetch(
      'eng_writingtools_examples',
      { data_type: 'example', is_active: true },
      'step_number',
      'asc',
      1000
    );
    
    if (!allRecords || allRecords.length === 0) {
      return [];
    }
    
    // Group records by topic and category
    const groupedByTopic = {};
    
    allRecords.forEach(record => {
      const topic = record.topic;
      const category = record.category;
      const key = `${category}_${topic}`;
      
      if (!groupedByTopic[key]) {
        groupedByTopic[key] = {
          topic: topic,
          category: category,
          steps: []
        };
      }
      
      // Add step to the steps array with step_number for proper ordering
      groupedByTopic[key].steps.push({
        label: record.step_label || '',
        content: record.step_content || '',
        stepNumber: record.step_number || 0
      });
    });
    
    // Convert grouped object to array and sort steps within each topic by step_number
    const examples = Object.values(groupedByTopic).map(item => {
      // Sort steps by step_number (1-15 within each topic)
      item.steps.sort((a, b) => a.stepNumber - b.stepNumber);
      
      // Remove stepNumber before returning
      item.steps = item.steps.map(step => ({
        label: step.label || '',
        content: step.content || ''
      }));
      
      return item;
    });
    
    return examples;
  } catch (error) {
    console.error('Error fetching examples:', error);
    throw error;
  }
};

/**
 * Save AI-generated example to Supabase
 * Inserts all steps (1-15) for the given example into the database
 */
export const saveExampleToSupabase = async (exampleData) => {
  try {
    const { topic, steps, category = 'UserDefined' } = exampleData;
    
    if (!topic || !steps || !Array.isArray(steps) || steps.length === 0) {
      throw new Error('Invalid example data: topic and steps are required');
    }

    // Prepare all step records
    const records = steps.map((step, index) => ({
      data_type: 'example',
      topic: topic,
      category: category,
      step_number: index + 1,
      step_label: step.label || '',
      step_content: step.content || '',
      source_file: 'ai_generated',
      sort_order: index + 1,
      is_active: true
    }));

    // Insert records one by one (Supabase REST API doesn't support batch inserts)
    const insertPromises = records.map(record => {
      return fetch(`${SUPABASE_URL}/rest/v1/eng_writingtools_examples`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify(record)
      });
    });

    const responses = await Promise.all(insertPromises);
    
    // Check if all inserts were successful
    const results = await Promise.all(responses.map(async (response, index) => {
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save step ${index + 1}: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return await response.json();
    }));

    return {
      success: true,
      message: `Successfully saved "${topic}" with ${steps.length} steps to database`,
      recordsInserted: results.length
    };
  } catch (error) {
    console.error('Error saving example to Supabase:', error);
    throw error;
  }
};

/**
 * Fetch examples by category from Supabase
 */
export const fetchExamplesByCategory = async (category) => {
  try {
    const data = await supabaseFetch(
      'eng_writingtools_examples',
      { data_type: 'example', category: category, is_active: true },
      'step_number',
      'asc',
      1000
    );
    
    if (!data || data.length === 0) {
      return [];
    }
    
    // Group by topic, maintaining sort order
    const groupedByTopic = {};
    
    data.forEach(record => {
      const topic = record.topic;
      const key = `${category}_${topic}`;
      
      if (!groupedByTopic[key]) {
        groupedByTopic[key] = {
          topic: topic,
          category: category,
          steps: []
        };
      }
      
      // Add step with step_number for proper ordering within topic
      groupedByTopic[key].steps.push({
        label: record.step_label || '',
        content: record.step_content || '',
        stepNumber: record.step_number || 0
      });
    });
    
    // Sort steps within each topic by step_number and remove stepNumber
    const examples = Object.values(groupedByTopic).map(item => {
      // Sort steps by step_number (1-15 within each topic)
      item.steps.sort((a, b) => a.stepNumber - b.stepNumber);
      
      // Remove stepNumber before returning and ensure proper structure
      const cleanedSteps = item.steps.map(step => ({
        label: step.label || '',
        content: step.content || ''
      }));
      
      return {
        topic: item.topic,
        category: item.category,
        steps: cleanedSteps
      };
    });
    
    // Debug logging
    if (examples.length > 0) {
      console.log(`[${category}] Loaded ${examples.length} examples`);
      console.log(`[${category}] First example steps:`, examples[0]?.steps?.length || 0);
    }
    
    return examples;
  } catch (error) {
    console.error(`Error fetching examples for category ${category}:`, error);
    throw error;
  }
};

/**
 * Save completed composition to Supabase
 * @param {Object} compositionData - The composition data to save
 * @param {string} compositionData.title - Composition title
 * @param {string} compositionData.style - Composition style (narrative, descriptive, etc.)
 * @param {number} compositionData.wordLength - Target word length
 * @param {string} compositionData.finalCompositionText - The final compiled composition text
 * @param {Array} compositionData.nodes - Array of node objects with:
 *   - name: string (node topic)
 *   - selectedSentences: array of objects with step_label and content
 * @returns {Promise<Object>} Success response with saved composition ID
 */
export const saveCompositionToSupabase = async (compositionData) => {
  try {
    const { title, style, wordLength, finalCompositionText, nodes } = compositionData;

    if (!title || !style || !wordLength || !finalCompositionText) {
      throw new Error('Invalid composition data: title, style, wordLength, and finalCompositionText are required');
    }

    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      throw new Error('Invalid composition data: nodes array is required');
    }

    // Prepare nodes_data in the required JSONB format
    // Structure: array of objects with node_name, node_order, and selected_sentences
    const nodesData = nodes.map((node, index) => {
      const selectedSentences = (node.selectedSentences || []).map(sentence => ({
        step_label: sentence.step_label || sentence.label || '',
        sentence_content: sentence.sentence_content || sentence.content || ''
      }));

      return {
        node_name: node.name || node.node_name || '',
        node_order: index + 1,
        selected_sentences: selectedSentences
      };
    });

    // Prepare the record for insertion
    const record = {
      title: title.trim(),
      style: style,
      word_length: parseInt(wordLength),
      final_composition_text: finalCompositionText,
      nodes_data: nodesData,
      category: 'AIComposition',
      is_active: true
    };

    // Insert into Supabase
    const response = await fetch(`${SUPABASE_URL}/rest/v1/eng_writingtools_composition`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(record)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save composition: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const result = await response.json();

    return {
      success: true,
      message: `Successfully saved composition "${title}" to database`,
      compositionId: result[0]?.id || null,
      data: result[0] || result
    };
  } catch (error) {
    console.error('Error saving composition to Supabase:', error);
    throw error;
  }
};

/**
 * Fetch all saved compositions from Supabase
 * @returns {Promise<Array>} Array of saved compositions
 */
export const fetchCompositions = async () => {
  try {
    const data = await supabaseFetch(
      'eng_writingtools_composition',
      { is_active: true },
      'created_at',
      'desc',
      100 // Limit to 100 most recent
    );

    if (!data || data.length === 0) {
      return [];
    }

    // Format the data for easier use
    return data.map(composition => ({
      id: composition.id,
      title: composition.title,
      style: composition.style,
      wordLength: composition.word_length,
      finalCompositionText: composition.final_composition_text,
      nodesData: composition.nodes_data || [],
      category: composition.category,
      createdAt: composition.created_at
    }));
  } catch (error) {
    console.error('Error fetching compositions:', error);
    throw error;
  }
};

/**
 * Fetch a single composition by ID from Supabase
 * @param {string} compositionId - The ID of the composition to fetch
 * @returns {Promise<Object>} The composition data
 */
export const fetchCompositionById = async (compositionId) => {
  try {
    const data = await supabaseFetch(
      'eng_writingtools_composition',
      { id: compositionId, is_active: true },
      'created_at',
      'desc',
      1
    );

    if (!data || data.length === 0) {
      throw new Error('Composition not found');
    }

    const composition = data[0];
    return {
      id: composition.id,
      title: composition.title,
      style: composition.style,
      wordLength: composition.word_length,
      finalCompositionText: composition.final_composition_text,
      nodesData: composition.nodes_data || [],
      category: composition.category,
      createdAt: composition.created_at
    };
  } catch (error) {
    console.error('Error fetching composition:', error);
    throw error;
  }
};
